/**
 * TasksPage — staff task tracker.
 *
 * Backend: /api/tasks/* (see /app/backend/routes/tasks.py)
 *   GET /api/tasks                 — list with filters (status, priority, assignee_id, scope, q)
 *   GET /api/tasks/stats           — dashboard counts
 *   GET /api/tasks/assignable-admins
 *   POST /api/tasks                — create
 *   PUT /api/tasks/{id}            — update (title/desc/assignee/priority/due)
 *   PUT /api/tasks/{id}/status     — change status (new|in_progress|completed|cancelled)
 *   POST /api/tasks/{id}/comments  — append comment
 *   DELETE /api/tasks/{id}         — soft-delete (archive)
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Plus, Loader2, Search, ListChecks, Clock, CheckCircle2, XCircle,
  AlertTriangle, Pencil, Trash2, MessageSquare, ChevronDown, User as UserIcon,
  Calendar as CalendarIcon, Flag, RotateCcw, Inbox,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  new:         { ar: 'جديدة',       en: 'New',         color: '#3b82f6' },
  in_progress: { ar: 'قيد التنفيذ', en: 'In progress', color: '#f59e0b' },
  completed:   { ar: 'مكتملة',      en: 'Completed',   color: '#10b981' },
  cancelled:   { ar: 'ملغاة',       en: 'Cancelled',   color: '#71717a' },
};

const PRIORITY_LABELS = {
  low:    { ar: 'منخفضة',  en: 'Low',     color: '#10b981' },
  medium: { ar: 'متوسطة',  en: 'Medium',  color: '#3b82f6' },
  high:   { ar: 'عالية',   en: 'High',    color: '#f59e0b' },
  urgent: { ar: 'عاجلة',   en: 'Urgent',  color: '#ef4444' },
};

const EMPTY_FORM = {
  title: '',
  description: '',
  assignee_id: '',
  priority: 'medium',
  due_date: '',
};

// Format ISO/date string as "DD MMM YYYY" (English digits, RTL-safe).
const fmtDate = (s, isAr) => {
  if (!s) return isAr ? '—' : '—';
  try {
    const d = s.length === 10 ? new Date(`${s}T00:00:00`) : new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return s;
  }
};

export default function TasksPage() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }),
    [token],
  );

  // --------- state ---------
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, new: 0, in_progress: 0, completed: 0, cancelled: 0, mine_open: 0 });
  const [admins, setAdmins] = useState([]);
  // filters
  const [fStatus, setFStatus] = useState('all');
  const [fPriority, setFPriority] = useState('all');
  const [fScope, setFScope] = useState('all'); // all | mine | created_by_me
  const [search, setSearch] = useState('');
  // modals
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [activeTask, setActiveTask] = useState(null);
  // forms
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [statusSaving, setStatusSaving] = useState(null); // task id
  const [commentDraft, setCommentDraft] = useState('');
  const [commentSaving, setCommentSaving] = useState(false);

  // --------- API ---------
  const fetchAdmins = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/tasks/assignable-admins`, { headers });
      setAdmins(res.data?.data || []);
    } catch (e) {
      // non-fatal
    }
  }, [headers]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/tasks/stats`, { headers });
      setStats(res.data || {});
    } catch (e) {
      // non-fatal
    }
  }, [headers]);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (fStatus !== 'all') params.status = fStatus;
      if (fPriority !== 'all') params.priority = fPriority;
      if (fScope === 'mine') params.scope = 'mine';
      if (fScope === 'created_by_me') params.scope = 'created_by_me';
      if (search.trim()) params.q = search.trim();
      const res = await axios.get(`${BACKEND_URL}/api/tasks`, { headers, params });
      setTasks(res.data?.data || []);
    } catch (e) {
      toast.error(isAr ? 'فشل في جلب المهام' : 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [headers, fStatus, fPriority, fScope, search, isAr]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchAdmins();
  }, [fetchAdmins]);

  useEffect(() => {
    fetchTasks();
    fetchStats();
  }, [fetchTasks, fetchStats]);

  // --------- handlers ---------
  const openCreate = () => {
    setForm(EMPTY_FORM);
    setShowCreate(true);
  };

  const openEdit = (t) => {
    setActiveTask(t);
    setForm({
      title: t.title || '',
      description: t.description || '',
      assignee_id: t.assignee_id || '',
      priority: t.priority || 'medium',
      due_date: t.due_date ? t.due_date.slice(0, 10) : '',
    });
    setShowEdit(true);
  };

  const openDetails = (t) => {
    setActiveTask(t);
    setCommentDraft('');
    setShowDetails(true);
  };

  const openDelete = (t) => {
    setActiveTask(t);
    setShowDelete(true);
  };

  const submitCreate = async () => {
    if (!form.title.trim() || form.title.trim().length < 2) {
      toast.error(isAr ? 'العنوان مطلوب' : 'Title is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        priority: form.priority,
        due_date: form.due_date || null,
      };
      if (form.assignee_id) payload.assignee_id = form.assignee_id;
      const res = await axios.post(`${BACKEND_URL}/api/tasks`, payload, { headers });
      toast.success(isAr ? 'تم إنشاء المهمة' : 'Task created');
      if (res.data?.data?.email_sent === false && res.data?.data?.assignee_email) {
        toast.warning(isAr ? 'تم إنشاء المهمة لكن فشل إرسال الإيميل للمُكلَّف' : 'Task created but assignee email failed');
      }
      setShowCreate(false);
      setForm(EMPTY_FORM);
      fetchTasks();
      fetchStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isAr ? 'فشل في الإنشاء' : 'Create failed'));
    } finally {
      setSaving(false);
    }
  };

  const submitEdit = async () => {
    if (!activeTask) return;
    if (!form.title.trim() || form.title.trim().length < 2) {
      toast.error(isAr ? 'العنوان مطلوب' : 'Title is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        priority: form.priority,
        due_date: form.due_date || null,
        assignee_id: form.assignee_id || '',
      };
      await axios.put(`${BACKEND_URL}/api/tasks/${activeTask.id}`, payload, { headers });
      toast.success(isAr ? 'تم تحديث المهمة' : 'Task updated');
      setShowEdit(false);
      setActiveTask(null);
      fetchTasks();
      fetchStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isAr ? 'فشل التحديث' : 'Update failed'));
    } finally {
      setSaving(false);
    }
  };

  const submitDelete = async () => {
    if (!activeTask) return;
    setSaving(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/tasks/${activeTask.id}`, { headers });
      toast.success(isAr ? 'تم أرشفة المهمة' : 'Task archived');
      setShowDelete(false);
      setActiveTask(null);
      fetchTasks();
      fetchStats();
    } catch (e) {
      toast.error(isAr ? 'فشل الحذف' : 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (taskId, nextStatus) => {
    setStatusSaving(taskId);
    try {
      await axios.put(
        `${BACKEND_URL}/api/tasks/${taskId}/status`,
        { status: nextStatus },
        { headers },
      );
      // optimistic patch
      setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, status: nextStatus } : t)));
      fetchStats();
    } catch (e) {
      toast.error(isAr ? 'فشل تغيير الحالة' : 'Status change failed');
    } finally {
      setStatusSaving(null);
    }
  };

  const submitComment = async () => {
    if (!activeTask || !commentDraft.trim()) return;
    setCommentSaving(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/tasks/${activeTask.id}/comments`,
        { text: commentDraft.trim() },
        { headers },
      );
      const newComment = res.data?.comment;
      if (newComment) {
        setActiveTask((prev) => prev ? { ...prev, comments: [...(prev.comments || []), newComment] } : prev);
      }
      setCommentDraft('');
    } catch (e) {
      toast.error(isAr ? 'فشل إضافة التعليق' : 'Add comment failed');
    } finally {
      setCommentSaving(false);
    }
  };

  // --------- render ---------
  const filterActive = fStatus !== 'all' || fPriority !== 'all' || fScope !== 'all' || !!search.trim();
  const resetFilters = () => {
    setFStatus('all');
    setFPriority('all');
    setFScope('all');
    setSearch('');
  };

  return (
    <DashboardLayout
      title="نظام المهام"
      titleEn="Tasks"
      subtitle="إنشاء وتكليف ومتابعة مهام فريق العمل"
      subtitleEn="Create, assign and track team tasks"
      screenCode="EMP-TASK"
      backPath="/employee-dashboard"
      onBack={() => navigate('/employee-dashboard')}
      breadcrumb={[
        { labelAr: 'لوحة تحكم الموظف', labelEn: 'Employee Dashboard', path: '/employee-dashboard' },
        { labelAr: 'المهام', labelEn: 'Tasks' },
      ]}
    >
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="tasks-page">

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <StatChip
            label={isAr ? 'الإجمالي' : 'Total'} value={stats.total} color="#d4af37"
            icon={ListChecks} testid="task-stat-total"
          />
          <StatChip
            label={isAr ? 'جديدة' : 'New'} value={stats.new} color="#3b82f6"
            icon={Inbox} testid="task-stat-new"
          />
          <StatChip
            label={isAr ? 'قيد التنفيذ' : 'In progress'} value={stats.in_progress} color="#f59e0b"
            icon={Clock} testid="task-stat-progress"
          />
          <StatChip
            label={isAr ? 'مكتملة' : 'Completed'} value={stats.completed} color="#10b981"
            icon={CheckCircle2} testid="task-stat-completed"
          />
          <StatChip
            label={isAr ? 'مهامي المفتوحة' : 'My open'} value={stats.mine_open} color="#a855f7"
            icon={Flag} testid="task-stat-mine"
          />
        </div>

        {/* Filter bar */}
        <div className="bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-4 mb-5">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
            {/* Search */}
            <div className="md:col-span-4 relative">
              <Search className="h-4 w-4 text-[#71717A] absolute top-1/2 -translate-y-1/2 start-3" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={isAr ? 'بحث في العنوان أو الوصف...' : 'Search title or description...'}
                className="bg-white/5 border-white/10 text-white ps-9"
                data-testid="task-search-input"
              />
            </div>
            {/* Status */}
            <div className="md:col-span-2">
              <Select value={fStatus} onValueChange={setFStatus}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="task-filter-status">
                  <SelectValue placeholder={isAr ? 'الحالة' : 'Status'} />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-white/10 text-white">
                  <SelectItem value="all">{isAr ? 'كل الحالات' : 'All statuses'}</SelectItem>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{isAr ? v.ar : v.en}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Priority */}
            <div className="md:col-span-2">
              <Select value={fPriority} onValueChange={setFPriority}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="task-filter-priority">
                  <SelectValue placeholder={isAr ? 'الأولوية' : 'Priority'} />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-white/10 text-white">
                  <SelectItem value="all">{isAr ? 'كل الأولويات' : 'All priorities'}</SelectItem>
                  {Object.entries(PRIORITY_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{isAr ? v.ar : v.en}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Scope */}
            <div className="md:col-span-2">
              <Select value={fScope} onValueChange={setFScope}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="task-filter-scope">
                  <SelectValue placeholder={isAr ? 'النطاق' : 'Scope'} />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-white/10 text-white">
                  <SelectItem value="all">{isAr ? 'كل المهام' : 'All tasks'}</SelectItem>
                  <SelectItem value="mine">{isAr ? 'مهامي' : 'Assigned to me'}</SelectItem>
                  <SelectItem value="created_by_me">{isAr ? 'أنشأتها' : 'Created by me'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 flex items-center justify-end gap-2">
              {filterActive && (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="flex items-center gap-1 text-xs text-[#A1A1AA] hover:text-white transition-colors"
                  data-testid="task-filter-reset"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  {isAr ? 'إعادة ضبط' : 'Reset'}
                </button>
              )}
              <Button
                onClick={openCreate}
                className="bg-[#d4af37] hover:bg-[#c9a227] text-black font-bold gap-2"
                data-testid="task-create-btn"
              >
                <Plus className="h-4 w-4" />
                {isAr ? 'مهمة جديدة' : 'New task'}
              </Button>
            </div>
          </div>
        </div>

        {/* Tasks list */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-10 w-10 animate-spin text-[#d4af37]" />
          </div>
        ) : tasks.length === 0 ? (
          <EmptyTasks isAr={isAr} onCreate={openCreate} />
        ) : (
          <div className="space-y-3" data-testid="task-list">
            {tasks.map((t, i) => (
              <TaskRow
                key={t.id}
                task={t}
                index={i}
                isAr={isAr}
                currentUserId={user?.id}
                statusSaving={statusSaving === t.id}
                onChangeStatus={(s) => changeStatus(t.id, s)}
                onEdit={() => openEdit(t)}
                onDelete={() => openDelete(t)}
                onOpen={() => openDetails(t)}
              />
            ))}
          </div>
        )}
      </section>

      {/* Create / Edit Modals */}
      <TaskFormModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        title={isAr ? 'إنشاء مهمة جديدة' : 'Create new task'}
        form={form}
        setForm={setForm}
        admins={admins}
        saving={saving}
        onSubmit={submitCreate}
        isAr={isAr}
        submitLabel={isAr ? 'إنشاء' : 'Create'}
        testidPrefix="create"
      />
      <TaskFormModal
        open={showEdit}
        onClose={() => { setShowEdit(false); setActiveTask(null); }}
        title={isAr ? 'تعديل المهمة' : 'Edit task'}
        form={form}
        setForm={setForm}
        admins={admins}
        saving={saving}
        onSubmit={submitEdit}
        isAr={isAr}
        submitLabel={isAr ? 'حفظ' : 'Save'}
        testidPrefix="edit"
      />

      {/* Delete confirm */}
      <Dialog open={showDelete} onOpenChange={(o) => { if (!o) { setShowDelete(false); setActiveTask(null); } }}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-md" data-testid="task-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              {isAr ? 'حذف / أرشفة المهمة' : 'Archive task'}
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              {isAr
                ? 'سيتم نقل المهمة إلى الأرشيف. لن تظهر في القوائم الحالية.'
                : 'The task will be archived and removed from the active lists.'}
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm text-white bg-white/5 border border-white/10 rounded-xl p-3">
            <span className="text-[#71717A] me-2">{isAr ? 'العنوان:' : 'Title:'}</span>
            {activeTask?.title}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDelete(false)} className="border-white/10 text-white hover:bg-white/5">
              {isAr ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={submitDelete}
              disabled={saving}
              className="bg-red-500 hover:bg-red-600 text-white gap-2"
              data-testid="task-delete-confirm"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              {isAr ? 'أرشفة' : 'Archive'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Details + comments */}
      <Dialog open={showDetails} onOpenChange={(o) => { if (!o) { setShowDetails(false); setActiveTask(null); } }}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="task-details-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg">
              {activeTask?.title}
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA] flex items-center gap-3 flex-wrap">
              <StatusPill status={activeTask?.status} isAr={isAr} />
              <PriorityPill priority={activeTask?.priority} isAr={isAr} />
              {activeTask?.due_date && (
                <span className="text-xs flex items-center gap-1">
                  <CalendarIcon className="h-3 w-3" />
                  {fmtDate(activeTask.due_date, isAr)}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {activeTask?.description && (
            <div className="text-sm text-white whitespace-pre-wrap bg-white/[0.02] border border-white/10 rounded-xl p-3">
              {activeTask.description}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 text-xs text-[#A1A1AA]">
            <div>
              <p className="text-[10px] text-[#71717A] mb-1">{isAr ? 'المُكلَّف' : 'Assignee'}</p>
              <p className="text-white flex items-center gap-1.5"><UserIcon className="h-3 w-3" /> {activeTask?.assignee_name || (isAr ? 'غير محدد' : 'Unassigned')}</p>
            </div>
            <div>
              <p className="text-[10px] text-[#71717A] mb-1">{isAr ? 'أنشأها' : 'Created by'}</p>
              <p className="text-white">{activeTask?.created_by_name || '—'}</p>
            </div>
          </div>

          {/* Comments */}
          <div className="space-y-2 mt-2">
            <p className="text-xs font-bold text-[#A1A1AA] flex items-center gap-1">
              <MessageSquare className="h-3.5 w-3.5" />
              {isAr ? 'التعليقات' : 'Comments'}
              <span className="text-[#71717A]">({activeTask?.comments?.length || 0})</span>
            </p>
            <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-1">
              {(activeTask?.comments || []).length === 0 && (
                <p className="text-xs text-[#71717A] italic">{isAr ? 'لا توجد تعليقات بعد.' : 'No comments yet.'}</p>
              )}
              {(activeTask?.comments || []).map((c) => (
                <div key={c.id} className="bg-white/[0.02] border border-white/10 rounded-xl p-3">
                  <div className="flex items-center justify-between mb-1 text-xs">
                    <span className="text-white font-medium">{c.author_name}</span>
                    <span className="text-[#71717A]">{fmtDate(c.at, isAr)}</span>
                  </div>
                  <p className="text-sm text-white whitespace-pre-wrap">{c.text}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={commentDraft}
                onChange={(e) => setCommentDraft(e.target.value)}
                placeholder={isAr ? 'أضف تعليقاً...' : 'Add a comment...'}
                className="bg-white/5 border-white/10 text-white"
                onKeyDown={(e) => { if (e.key === 'Enter' && commentDraft.trim()) submitComment(); }}
                data-testid="task-comment-input"
              />
              <Button
                onClick={submitComment}
                disabled={!commentDraft.trim() || commentSaving}
                className="bg-[#d4af37] hover:bg-[#c9a227] text-black gap-2"
                data-testid="task-comment-submit"
              >
                {commentSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquare className="h-4 w-4" />}
                {isAr ? 'إرسال' : 'Post'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

// ---------- subcomponents ----------

function StatChip({ label, value, color, icon: Icon, testid }) {
  return (
    <div
      className="bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-4"
      data-testid={testid}
    >
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center border"
          style={{ backgroundColor: `${color}15`, borderColor: `${color}40` }}
        >
          <Icon className="h-5 w-5" style={{ color }} />
        </div>
        <div>
          <p className="text-xs text-[#71717A]">{label}</p>
          <p className="text-xl font-bold text-white">{value ?? 0}</p>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status, isAr }) {
  if (!status) return null;
  const s = STATUS_LABELS[status] || STATUS_LABELS.new;
  return (
    <span
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold border"
      style={{ color: s.color, backgroundColor: `${s.color}15`, borderColor: `${s.color}40` }}
    >
      {isAr ? s.ar : s.en}
    </span>
  );
}

function PriorityPill({ priority, isAr }) {
  if (!priority) return null;
  const p = PRIORITY_LABELS[priority] || PRIORITY_LABELS.medium;
  return (
    <span
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold border"
      style={{ color: p.color, backgroundColor: `${p.color}15`, borderColor: `${p.color}40` }}
    >
      <Flag className="h-3 w-3" />
      {isAr ? p.ar : p.en}
    </span>
  );
}

function TaskRow({
  task, index, isAr, currentUserId, statusSaving, onChangeStatus, onEdit, onDelete, onOpen,
}) {
  const overdue = useMemo(() => {
    if (!task.due_date || task.status === 'completed' || task.status === 'cancelled') return false;
    try {
      const d = task.due_date.length === 10 ? new Date(`${task.due_date}T00:00:00`) : new Date(task.due_date);
      return d.getTime() < Date.now();
    } catch {
      return false;
    }
  }, [task]);
  const isMine = task.assignee_id === currentUserId;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.04, 0.4) }}
      className={`bg-white/[0.02] backdrop-blur-xl border rounded-2xl p-4 hover:bg-white/[0.04] transition-colors ${
        overdue ? 'border-red-500/30' : 'border-white/10'
      }`}
      data-testid={`task-row-${task.id}`}
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
        {/* Title + description */}
        <div className="md:col-span-5">
          <button
            onClick={onOpen}
            className="text-start group w-full"
            data-testid="task-open-details"
          >
            <p className="text-white font-bold text-base group-hover:text-[#d4af37] transition-colors">{task.title}</p>
            {task.description && (
              <p className="text-xs text-[#A1A1AA] mt-1 line-clamp-2">{task.description}</p>
            )}
            {(task.comments?.length || 0) > 0 && (
              <p className="text-[10px] text-[#71717A] mt-1 flex items-center gap-1">
                <MessageSquare className="h-3 w-3" />
                {task.comments.length} {isAr ? 'تعليق' : 'comments'}
              </p>
            )}
          </button>
        </div>

        {/* Status + priority */}
        <div className="md:col-span-2 flex flex-wrap gap-1.5">
          <StatusPill status={task.status} isAr={isAr} />
          <PriorityPill priority={task.priority} isAr={isAr} />
          {isMine && (
            <span className="text-[10px] px-2 py-1 rounded-full bg-purple-500/15 border border-purple-500/40 text-purple-300 font-bold">
              {isAr ? 'لي' : 'Mine'}
            </span>
          )}
        </div>

        {/* Assignee */}
        <div className="md:col-span-2">
          <p className="text-[10px] text-[#71717A] mb-0.5">{isAr ? 'المُكلَّف' : 'Assignee'}</p>
          <p className="text-xs text-white flex items-center gap-1 truncate">
            <UserIcon className="h-3 w-3 flex-shrink-0" />
            {task.assignee_name || <span className="text-[#71717A]">{isAr ? 'غير محدد' : 'Unassigned'}</span>}
          </p>
        </div>

        {/* Due date */}
        <div className="md:col-span-1">
          <p className="text-[10px] text-[#71717A] mb-0.5">{isAr ? 'الاستحقاق' : 'Due'}</p>
          <p className={`text-xs flex items-center gap-1 ${overdue ? 'text-red-400 font-bold' : 'text-white'}`}>
            <CalendarIcon className="h-3 w-3" />
            {task.due_date ? fmtDate(task.due_date, isAr) : '—'}
          </p>
        </div>

        {/* Actions */}
        <div className="md:col-span-2 flex items-center justify-end gap-2">
          <div className="relative">
            <select
              value={task.status}
              onChange={(e) => onChangeStatus(e.target.value)}
              disabled={statusSaving}
              className="appearance-none bg-white/5 border border-white/10 text-white text-xs rounded-lg ps-2 pe-7 py-1.5 cursor-pointer hover:bg-white/10 focus:outline-none focus:ring-1 focus:ring-[#d4af37]"
              data-testid="task-status-select"
            >
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <option key={k} value={k} className="bg-[#0a0a0a]">{isAr ? v.ar : v.en}</option>
              ))}
            </select>
            <ChevronDown className="h-3 w-3 text-[#71717A] absolute top-1/2 -translate-y-1/2 end-1 pointer-events-none" />
            {statusSaving && (
              <Loader2 className="h-3 w-3 animate-spin text-[#d4af37] absolute -end-5 top-1/2 -translate-y-1/2" />
            )}
          </div>
          <button
            type="button"
            onClick={onEdit}
            className="p-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 text-[#A1A1AA] hover:text-white transition-colors"
            title={isAr ? 'تعديل' : 'Edit'}
            data-testid="task-edit-btn"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="p-2 rounded-lg bg-red-500/10 border border-red-500/30 hover:bg-red-500/20 text-red-400 transition-colors"
            title={isAr ? 'أرشفة' : 'Archive'}
            data-testid="task-delete-btn"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function TaskFormModal({
  open, onClose, title, form, setForm, admins, saving, onSubmit, isAr, submitLabel, testidPrefix,
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="bg-[#0a0a0a] border-white/10 text-white max-w-xl max-h-[90vh] overflow-y-auto"
        data-testid={`task-${testidPrefix}-dialog`}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListChecks className="h-5 w-5 text-[#d4af37]" />
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Title */}
          <div>
            <label className="text-sm text-[#A1A1AA] mb-2 block">{isAr ? 'العنوان *' : 'Title *'}</label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder={isAr ? 'مثال: متابعة طلب العميل ALPHA' : 'e.g., Follow up on ALPHA order'}
              className="bg-white/5 border-white/10 text-white"
              data-testid={`task-${testidPrefix}-title`}
            />
          </div>

          {/* Description */}
          <div>
            <label className="text-sm text-[#A1A1AA] mb-2 block">{isAr ? 'الوصف' : 'Description'}</label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder={isAr ? 'تفاصيل المهمة...' : 'Task details...'}
              rows={4}
              className="bg-white/5 border-white/10 text-white"
              data-testid={`task-${testidPrefix}-description`}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            {/* Assignee */}
            <div>
              <label className="text-sm text-[#A1A1AA] mb-2 block">{isAr ? 'المُكلَّف' : 'Assignee'}</label>
              <Select
                value={form.assignee_id || '__none__'}
                onValueChange={(v) => setForm({ ...form, assignee_id: v === '__none__' ? '' : v })}
              >
                <SelectTrigger
                  className="bg-white/5 border-white/10 text-white"
                  data-testid={`task-${testidPrefix}-assignee`}
                >
                  <SelectValue placeholder={isAr ? 'اختر' : 'Select'} />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-white/10 text-white">
                  <SelectItem value="__none__">{isAr ? 'بدون تكليف' : 'Unassigned'}</SelectItem>
                  {admins.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name} <span className="text-[#71717A] text-xs">({a.email})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Priority */}
            <div>
              <label className="text-sm text-[#A1A1AA] mb-2 block">{isAr ? 'الأولوية' : 'Priority'}</label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger
                  className="bg-white/5 border-white/10 text-white"
                  data-testid={`task-${testidPrefix}-priority`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-white/10 text-white">
                  {Object.entries(PRIORITY_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{isAr ? v.ar : v.en}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Due date */}
          <div>
            <label className="text-sm text-[#A1A1AA] mb-2 block">{isAr ? 'تاريخ الاستحقاق' : 'Due date'}</label>
            <Input
              type="date"
              value={form.due_date}
              onChange={(e) => setForm({ ...form, due_date: e.target.value })}
              className="bg-white/5 border-white/10 text-white"
              dir="ltr"
              data-testid={`task-${testidPrefix}-due`}
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="border-white/10 text-white hover:bg-white/5">
            {isAr ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button
            onClick={onSubmit}
            disabled={saving}
            className="bg-[#d4af37] hover:bg-[#c9a227] text-black font-bold gap-2"
            data-testid={`task-${testidPrefix}-submit`}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmptyTasks({ isAr, onCreate }) {
  return (
    <div className="bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-12 text-center" data-testid="tasks-empty">
      <div
        className="w-20 h-20 rounded-2xl mx-auto mb-4 flex items-center justify-center border"
        style={{ backgroundColor: 'rgba(16, 185, 129, 0.10)', borderColor: 'rgba(16, 185, 129, 0.30)' }}
      >
        <ListChecks className="h-10 w-10 text-emerald-400" />
      </div>
      <h3 className="text-xl font-bold text-white mb-1">
        {isAr ? 'لا توجد مهام بعد' : 'No tasks yet'}
      </h3>
      <p className="text-sm text-[#A1A1AA] mb-5">
        {isAr ? 'ابدأ بإنشاء أول مهمة لفريقك.' : 'Start by creating the first task for your team.'}
      </p>
      <Button onClick={onCreate} className="bg-[#d4af37] hover:bg-[#c9a227] text-black font-bold gap-2" data-testid="tasks-empty-create">
        <Plus className="h-4 w-4" />
        {isAr ? 'مهمة جديدة' : 'New task'}
      </Button>
    </div>
  );
}
